// /max (not the default /min build) is required for getType() - see the
// phoneNumber branch below, which rejects valid-but-non-mobile numbers
// (toll-free, VoIP, pager, ...). A user's "Phone number" field is their
// personal contact number, not a business line, so a legitimately valid
// Indian 13-digit toll-free (1800...) number still isn't an acceptable
// value here even though isValidPhoneNumber alone would allow it.
import { parsePhoneNumberFromString, validatePhoneNumberLength } from "libphonenumber-js/max";
import {
  evaluateCondition,
  evaluateDisabledCondition,
  evaluateFieldVisibility,
  evaluateSectionVisibility,
} from "./evaluateCondition";

// FIXED_LINE_OR_MOBILE covers countries (e.g. the US/Canada numbering
// plan) whose pattern can't distinguish mobile from landline - treating it
// as mobile-acceptable avoids wrongly rejecting real mobile numbers there.
const MOBILE_PHONE_NUMBER_TYPES = new Set(["MOBILE", "FIXED_LINE_OR_MOBILE"]);

// Ported from src/utils/form/buildValidationSchema.js's per-field validator
// switch, adapted to return react-hook-form's resolver error shape
// ({ [name]: { type, message } }) instead of a { [name]: (value, formData)
// => string } map. Same field-descriptor contract, same per-type rules -
// no schema authored for the old engine needs to change to run here.
// Deliberately duplicated, not imported - see the comment in
// ./evaluateCondition.js for why.

const isEffectivelyEmpty = (val) => {
  if (val === undefined || val === null || val === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;

  if (typeof val === "object" && val !== null) {
    const hasValue = Object.keys(val).some((key) => {
      const v = val[key];
      return v !== undefined && v !== null && v !== "";
    });
    return !hasValue;
  }

  return false;
};

function validateField(field, value, formData, schema, mode) {
  const isDisabled = evaluateDisabledCondition(field, formData, schema, mode);
  if (isDisabled) return "";

  let isRequired = false;
  if (typeof field.required === "function") {
    isRequired = field.required(formData);
  } else if (field.requiredCondition && typeof field.requiredCondition === "object") {
    isRequired = evaluateCondition(field.requiredCondition, formData, schema);
  } else {
    isRequired = Boolean(field.required);
  }

  // A field with a non-empty default is assumed pre-filled; don't nag for
  // "required" before the user has had a chance to touch it.
  if (
    isRequired &&
    field.defaultValue !== undefined &&
    field.defaultValue !== null &&
    field.defaultValue !== ""
  ) {
    isRequired = false;
  }

  if (field.type === "phoneNumber") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;

    if (value) {
      // Matches PhoneInput's own parseValue exactly (split on the first
      // space only, dial code first, no upfront trim). A blanket
      // `value.trim()` before splitting - the old approach - swallows the
      // leading space PhoneInput sends when no country is picked yet
      // (composeValue produces " <digits>" while dialCode is still ""),
      // which silently misread the typed digits as the country code and
      // left `phone` empty - so "typed a number, forgot the country code"
      // never surfaced as an error at all.
      const [countryCode = "", ...rest] = value.split(" ");
      const phone = rest.join(" ").trim();

      if (!phone) {
        if (isRequired) return `${field.label} is required`;
        return "";
      }

      // Below here a phone digit has actually been typed, so these checks
      // apply regardless of whether the whole field is required - a
      // half-filled phone number (digits with no country code, or digits
      // that don't fit the selected country) is never a valid value to
      // save, required or not.
      if (!/^\+\d+$/.test(countryCode)) {
        return "Please select a country code";
      }

      if (!/^\d+$/.test(phone)) {
        return "Phone number must contain digits only";
      }

      // Per-country validation (libphonenumber-js) - e.g. an Indian (+91)
      // mobile number is always 10 digits, so this rejects something like
      // "+91 98989898989090" that a flat field.maxLength never would
      // unless every org remembered to configure it per country, which
      // isn't possible with a single scalar value.
      //
      // Checking the parsed number's type (not just isValid()/length) is
      // required here - a plain validity check only asks "is this a real,
      // dialable number for this country" across every number type
      // (mobile, toll-free, UAN, VoIP, pager, ...), so e.g. a 13-digit
      // Indian toll-free (1800...) number passes that check despite not
      // being a mobile number, which isn't an acceptable value for a
      // person's own phone number field.
      const full = `${countryCode}${phone}`;
      const parsed = parsePhoneNumberFromString(full);
      const numberType = parsed?.getType();
      if (!parsed || !parsed.isValid() || !MOBILE_PHONE_NUMBER_TYPES.has(numberType)) {
        if (parsed?.isValid()) {
          return `Please enter a mobile number, not a ${numberType.replace(/_/g, " ").toLowerCase()} number`;
        }
        const lengthResult = validatePhoneNumberLength(full);
        if (lengthResult === "TOO_SHORT") {
          return `Phone number is too short for ${countryCode}`;
        }
        if (lengthResult === "TOO_LONG") {
          return `Phone number is too long for ${countryCode}`;
        }
        return `Phone number is not valid for ${countryCode}`;
      }

      if (field.minLength && phone.length < field.minLength) {
        return `${field.label} must be at least ${field.minLength} digits`;
      }
      if (field.maxLength && phone.length > field.maxLength) {
        return `${field.label} must be at most ${field.maxLength} digits`;
      }
    }
    return "";
  }

  if (field.type === "arrayText" || field.type === "listManager") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;
    if (field.minLength && value && value.length < field.minLength) {
      return `${field.label} must have at least ${field.minLength} items`;
    }
    if (field.maxLength && value && value.length > field.maxLength) {
      return `${field.label} must have at most ${field.maxLength} items`;
    }
    return "";
  }

  if (field.type === "imageUpload") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;
    return "";
  }

  if (field.type === "number" || field.type === "numberWithDecimal") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;
    if (value !== undefined && value !== null && value !== "") {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return `${field.label} must be a valid number`;
      if (field.minLength && numValue < field.minLength) {
        return `${field.label} must be at least ${field.minLength}`;
      }
      if (field.maxLength && numValue > field.maxLength) {
        return `${field.label} must be at most ${field.maxLength}`;
      }
    }
    return "";
  }

  if (["text", "email", "url", "textArea", "textEditor", "richEditor"].includes(field.type)) {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;

    if (value) {
      if (field.type === "text" && field.minLength && value.length < field.minLength) {
        return `${field.label} must be at least ${field.minLength} characters`;
      }
      if (field.type === "text" && field.maxLength && value.length > field.maxLength) {
        return `${field.label} must be at most ${field.maxLength} characters`;
      }
      if (field.pattern && field.pattern !== "") {
        try {
          const regex = new RegExp(field.pattern);
          if (!regex.test(value)) return `Invalid ${field.label}`;
        } catch {
          // Invalid regex authored via the org's field customization -
          // skip pattern validation rather than crash the form.
        }
      }
    }
    return "";
  }

  if (
    field.type === "singleSelect" ||
    field.type === "multiSelect" ||
    field.type === "company" ||
    field.type === "market" ||
    field.type === "simplifyUser"
  ) {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;
    return "";
  }

  if (field.type === "checkbox" || field.type === "checkboxContainer") {
    if (isRequired && !value) return `${field.label || field.name} is required`;
    return "";
  }

  if (field.type === "radioButton") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;
    return "";
  }

  if (field.type === "date" || field.type === "dateAndTime") {
    if (isRequired && isEffectivelyEmpty(value)) return `${field.label} is required`;

    if (value) {
      const dateValue = value instanceof Date ? value : new Date(value);
      const minChars = field.type === "date" ? 10 : 16;

      if (isNaN(dateValue.getTime())) {
        if (typeof value === "string" && value.length > 0 && value.length < minChars) {
          return ""; // partial date/time being typed - don't flag yet
        }
        return field.type === "date"
          ? `${field.label} must be a valid date`
          : `${field.label} must be a valid date and time`;
      }

      const boundary = field.type === "date" ? new Date().setHours(0, 0, 0, 0) : Date.now();
      if (field.noPastDates && dateValue.getTime() < boundary) {
        return `${field.label} cannot be in the past`;
      }
      if (field.noFutureDates && dateValue.getTime() > boundary) {
        return `${field.label} cannot be in the future`;
      }
    }
    return "";
  }

  return "";
}

export function buildResolver(schema, mode = "create") {
  return async (values) => {
    const errors = {};

    if (Array.isArray(schema)) {
      schema.forEach((section) => {
        // A field the user can't currently see - because its own section
        // is conditionally hidden (e.g. CompanyFormModal's shipping
        // address, hidden while "same as billing" is checked), or the
        // field itself is (a per-origin field for an unselected origin,
        // etc.) - can't be required to fill in. Same visibility rules
        // DynamicForm renders by, so validation never disagrees with what
        // the user can actually see and edit.
        if (!evaluateSectionVisibility(section, values, schema)) return;

        (section.fields || []).forEach((field) => {
          if (!field?.name) return;
          if (!evaluateFieldVisibility(field, values, schema)) return;
          const message = validateField(field, values[field.name], values, schema, mode);
          if (message) {
            errors[field.name] = { type: "validate", message };
          }
        });
      });
    }

    return { values, errors };
  };
}
